// Desc: Page to display the list of spreadsheets
// Updated: 29/08/2025

import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../app/providers/AuthProvider";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import EmptyTemplate from "../../../shared/assets/ilustrations/EmptyTemplates.png";
import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import ThemeColors from "../../../shared/components/ThemeColors/ThemeColors";
import Titles from "../../../shared/components/Titles/Titles";

import { DeleteListagens } from "../../../services/api/Listagens/DeleteListagens";
import { GetListagens } from "../../../services/api/Listagens/GetListagens";
import { truncateText } from "../../../shared/utils/utils";
import ModalR from "../../../shared/components/Modals/ModalR";
import use123zapStore from "../../../context/123zapStore";
import { consultRegisteredIntegrations } from "../../../services/api/Integrations/listRegisteredIntegrations";
import StatusLabel from "../../../shared/components/StatusLabel/StatusLabel";
import themeColors from "../../../shared/components/ThemeColors/ThemeColors";
import SimpleTable from "../../../shared/components/common/ui/SimpleTable";
import IconResolver from "../../../shared/components/common/ui/IconResolver";
import {
  normalizeIntegrationValue,
  resolveIntegrationIcon,
  resolveIntegrationKey,
  resolveIntegrationTitle,
} from "../../../shared/constants/integrations";

function Sheets() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const sheets = use123zapStore((state) => state.sheets);
  const setSheets = use123zapStore((state) => state.setSheets);

  const alreadyFetchSheets = use123zapStore(
    (state) => state.alreadyFetchSheets
  );
  const setAlreadyFetchSheets = use123zapStore(
    (state) => state.setAlreadyFetchSheets
  );

  const setRegisteredERPS = use123zapStore((state) => state.setRegisteredERPS);
  const alreadyFetchRegisteredERPS = use123zapStore(
    (state) => state.alreadyFetchRegisteredERPS
  );
  const setAlreadyFetchRegisteredERPS = use123zapStore(
    (state) => state.setAlreadyFetchRegisteredERPS
  );

  const [loading, setLoading] = useState(!alreadyFetchSheets);
  const [isFetching, setIsFetching] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedList, setSelectedList] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setIsFetching(true);

      const resultJson = await GetListagens();

      const listagens = (resultJson?.data || []).map((item) => ({
        id: item.code,
        title: item.title,
        desc: truncateText(item.description, 50),
        link: item.url,
        type: item.type,
        icon: item.type,
      }));

      setSheets(listagens);
      setAlreadyFetchSheets(true);
    } catch (error) {
      console.error("Erro ao obter dados da API:", error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [setSheets, setAlreadyFetchSheets]);

  useEffect(() => {
    if (!isAuthenticated || alreadyFetchSheets) return;
    fetchData();
  }, [isAuthenticated, alreadyFetchSheets, fetchData]);

  useEffect(() => {
    if (!isAuthenticated || alreadyFetchRegisteredERPS) return;

    consultRegisteredIntegrations().then((response) => {
      const uniqueErps = Array.from(
        new Map(
          response.data
            .filter((item) => item?.integration_title)
            .map((item) => [
              resolveIntegrationKey(item.integration_title) ||
                normalizeIntegrationValue(item.integration_title),
              item,
            ]),
        ).values(),
      );
      const mappedData = uniqueErps.map((item) => ({
        title: resolveIntegrationTitle(item.integration_title),
        icon: item.integration_logo || resolveIntegrationIcon(item.integration_title),
        onClick: () => {},
        pago: () => {},
      }));

      setRegisteredERPS(mappedData);
      setAlreadyFetchRegisteredERPS(true);
    });
  }, [
    isAuthenticated,
    alreadyFetchRegisteredERPS,
    setRegisteredERPS,
    setAlreadyFetchRegisteredERPS,
  ]);

  const handleDelete = async (item) => {
    try {
      const resultJson = await DeleteListagens(item.id);

      if (
        resultJson?.message === "success" ||
        resultJson?.status === "success"
      ) {
        setOpenModalDelete(false);
        fetchData();
      }
    } catch (error) {
      console.error("Erro ao excluir listagem:", error);
    }
  };

  const handlePageAdd = () => {
    navigate("/listagens/adicionar");
  };

  return (
    <Fragment>
      <Box width={"100%"} height={"100vh"}>
        <HeaderPage
          variant="listing"
          title="Listagens"
          description="Organize bases de contatos e dados que alimentam suas campanhas."
        />

        {!loading &&
        sheets.length === 0 &&
        alreadyFetchSheets ? (
          <Box
            display="flex"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "90%",
            }}
          >
            <Box sx={{ width: { xs: "80%", sm: "auto" } }}>
              <Box
                component="img"
                src={EmptyTemplate}
                alt="Você não tem modelos"
                sx={{
                  width: { xs: "247px", sm: "494px" },
                  height: { xs: "165px", sm: "330px" },
                }}
              />

              <Box
                display="flex"
                sx={{
                  flexDirection: "column",
                  gap: "10px",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Titles
                  bold
                  type="H3"
                  color="#8033F4"
                >
                  Parece que você não tem planilhas :(
                </Titles>

                <Titles type="P1">
                  Importe uma agora mesmo, é fácil e rápido
                </Titles>

                <Button
                  variant="contained"
                  sx={{
                    color: "#fff",
                    width: "100%",
                    padding: "10px",
                    marginTop: "10px",
                  }}
                  onClick={handlePageAdd}
                >
                  Importar Listagem
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <SimpleTable
            tableName="Listagens"
            loading={loading}
            isFetching={isFetching}
            onRefresh={fetchData}
            data={sheets}
            columns={[
              {
                field: "icon",
                headerName: "",
                width: 80,
                sortable: false,
                renderCell: (params) => (
                  <IconResolver
                    name={params.value}
                    color={themeColors.purple500}
                    size={18}
                  />
                ),
              },
              {
                field: "title",
                headerName: "Título",
                flex: 1,
                minWidth: 200,
              },
              {
                field: "desc",
                headerName: "Descrição",
                flex: 1.5,
                minWidth: 320,
              },
              {
                field: "type",
                headerName: "Tipo",
                flex: 1,
                minWidth: 160,
                renderCell: (params) => (
                  <StatusLabel status={params.value} usingOnTable />
                ),
              },
            ]}
            actions={[
              {
                label: "Visualizar",
                icon: "file",
                color: "primary",
                isVisible: (row) =>
                  row?.type === "Google planilhas",
                onClick: (row) =>
                  window.open(row.link, "_blank"),
              },
              {
                label: "Excluir",
                icon: "delete",
                color: "error",
                isVisible: () => true,
                onClick: (row) => {
                  setSelectedList(row);
                  setOpenModalDelete(true);
                },
              },
            ]}
            redirect={handlePageAdd}
            redirectText="Adicionar Listagem"
            variant="listing"
          />
        )}
      </Box>

      <ModalR
        cancelText="Mudei de ideia"
        color={ThemeColors.purple500}
        confirmText="Sim, desejo excluir"
        subTitle="Esta ação não poderá ser desfeita"
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        onConfirm={
          selectedList ? () => handleDelete(selectedList) : null
        }
        title="Tem certeza que deseja excluir?"
        emoji="emoji10"
        iconColor={ThemeColors.purple400}
      />
    </Fragment>
  );
}

export default Sheets;
